// Pure helpers for the Surveys redesign. No API calls here; callers pass rows in.
export type StatusKey = '' | 'Test' | 'Open' | 'Hold' | 'Wait' | 'Close' | 'Error';

export const STATUS_TABS: { key: StatusKey; label: string; adminOnly?: boolean }[] = [
  { key: '', label: 'All' },
  { key: 'Test', label: 'Test' },
  { key: 'Open', label: 'Live' },
  { key: 'Hold', label: 'On Hold' },
  { key: 'Wait', label: 'Ids Awaited' },
  { key: 'Close', label: 'Ended' },
  { key: 'Error', label: 'Error', adminOnly: true },
];

export const visibleStatusTabs = (role?: string) =>
  STATUS_TABS.filter((t) => !t.adminOnly || role === 'admin' || role === 'super_admin');

// Same wording as the list's per-row status select.
const STATUS_LABELS: Record<string, string> = {
  Test: 'Test', Hold: 'On Hold', Open: 'Live', Wait: 'Ids awaited', Close: 'End', Error: 'Error',
};
export const statusLabel = (key?: string): string => (key ? STATUS_LABELS[key] ?? key : '-');

export const marketCodes = (parent: { country?: string; children?: any[] }): string => {
  const kids = parent.children || [];
  if (!kids.length) return parent.country || '-';
  const codes = kids.map((c) => c.shortName || c.country).filter(Boolean);
  return Array.from(new Set(codes)).join(', ');
};

export type InFieldRow = {
  surveyId?: string; shortName?: string; status?: string;
  completes?: number; terminates?: number; dropouts?: number; totalHits?: number;
  overQuota?: number; qualityTerminated?: number; preScreenTerminated?: number;
  verisoulBlocked?: number; verisoul_terminate_count?: number;
};

export type Kpis = { completes: number; hits: number; quota: number; irPct: number; dropoutPct: number; pctComplete: number };

// IR % and Dropout % use the same formulas as the list (DashboardSurvey render()).
export const kpisFromInFieldStats = (rows: InFieldRow[], quota: number): Kpis => {
  const t = rows.reduce<{ completes: number; terminates: number; dropouts: number; hits: number }>(
    (a, r) => ({
      completes: a.completes + (r.completes || 0),
      terminates: a.terminates + (r.terminates || 0),
      dropouts: a.dropouts + (r.dropouts || 0),
      hits: a.hits + (r.totalHits || 0),
    }),
    { completes: 0, terminates: 0, dropouts: 0, hits: 0 },
  );
  const q = Number(quota) || 0;
  return {
    completes: t.completes,
    hits: t.hits,
    quota: q,
    irPct: t.completes + t.terminates > 0 ? (t.completes / (t.completes + t.terminates)) * 100 : 0,
    dropoutPct: t.hits > 0 ? (t.dropouts / t.hits) * 100 : 0,
    pctComplete: q > 0 ? Math.min(100, (t.completes / q) * 100) : 0,
  };
};

export type Progress = Kpis & {
  terminates: number; overQuota: number; qualityTerminated: number;
  pqTerminated: number; verisoulBlocked: number; dropouts: number;
};

// The Fieldwork progress grid: the Overview KPIs plus every outcome bucket,
// summed from the same In-Field rows (verisoul per row as InFieldStats reads it).
export const progressFromInFieldStats = (rows: InFieldRow[], quota: number): Progress => {
  const sum = (pick: (r: InFieldRow) => number | undefined) => rows.reduce((a, r) => a + (Number(pick(r)) || 0), 0);
  return {
    ...kpisFromInFieldStats(rows, quota),
    terminates: sum((r) => r.terminates),
    overQuota: sum((r) => r.overQuota),
    qualityTerminated: sum((r) => r.qualityTerminated),
    pqTerminated: sum((r) => r.preScreenTerminated),
    verisoulBlocked: sum((r) => r.verisoulBlocked ?? r.verisoul_terminate_count),
    dropouts: sum((r) => r.dropouts),
  };
};

// getOneDXPanelOnly counters, as the panel grid reads them.
export type PanelCounts = {
  completed_count?: number | string; terminated_count?: number | string; over_quota_count?: number | string;
  quality_terminated_count?: number | string; pq_terminated_count?: number | string;
  verisoul_terminate_count?: number | string; totalhit_count?: number | string;
  dropout_count?: number | string; finalLOI?: number | string;
};

// Total row of the panel grid: counters summed, LOI weighted by completes.
export const sumPanelCounts = (rows: PanelCounts[]): PanelCounts => {
  const keys: (keyof PanelCounts)[] = [
    'completed_count', 'terminated_count', 'over_quota_count', 'quality_terminated_count',
    'pq_terminated_count', 'verisoul_terminate_count', 'totalhit_count', 'dropout_count',
  ];
  const out: PanelCounts = {};
  keys.forEach((k) => { out[k] = rows.reduce((a, r) => a + (Number(r[k]) || 0), 0); });
  out.finalLOI = weightedLoi(rows) ?? 0;
  return out;
};

export const statsByMarket = (rows: InFieldRow[]) => {
  const acc: Record<string, { completes: number; terminates: number; hits: number }> = {};
  rows.forEach((r) => {
    const k = r.shortName || '-';
    const cur = acc[k] || { completes: 0, terminates: 0, hits: 0 };
    acc[k] = {
      completes: cur.completes + (r.completes || 0),
      terminates: cur.terminates + (r.terminates || 0),
      hits: cur.hits + (r.totalHits || 0),
    };
  });
  const out: Record<string, { completes: number; hits: number; irPct: number }> = {};
  Object.keys(acc).forEach((k) => {
    const { completes, terminates, hits } = acc[k];
    out[k] = { completes, hits, irPct: completes + terminates > 0 ? (completes / (completes + terminates)) * 100 : 0 };
  });
  return out;
};

// Per child survey (not per country), so same-country siblings (JP, JP 2) stay apart.
export const statsBySurvey = (rows: InFieldRow[]) => {
  const acc: Record<string, { completes: number; terminates: number; hits: number }> = {};
  rows.forEach((r) => {
    if (!r.surveyId) return;
    const cur = acc[r.surveyId] || { completes: 0, terminates: 0, hits: 0 };
    acc[r.surveyId] = {
      completes: cur.completes + (r.completes || 0),
      terminates: cur.terminates + (r.terminates || 0),
      hits: cur.hits + (r.totalHits || 0),
    };
  });
  const out: Record<string, { completes: number; hits: number; irPct: number }> = {};
  Object.keys(acc).forEach((k) => {
    const { completes, terminates, hits } = acc[k];
    out[k] = { completes, hits, irPct: completes + terminates > 0 ? (completes / (completes + terminates)) * 100 : 0 };
  });
  return out;
};

// Same rule as Form.tsx's country tabs: siblings sharing a shortName are ordered by
// createdAt ascending; the first keeps the code, later ones get " 2", " 3", ...
export const marketLabels = (children: { id: string; shortName?: string; createdAt?: string }[]): Record<string, string> => {
  const groups: Record<string, { id: string; at: number; i: number }[]> = {};
  children.forEach((c, i) => {
    const code = c.shortName || '-';
    const at = c.createdAt ? new Date(c.createdAt).valueOf() : Number.MAX_SAFE_INTEGER;
    (groups[code] = groups[code] || []).push({ id: c.id, at: isNaN(at) ? Number.MAX_SAFE_INTEGER : at, i });
  });
  const out: Record<string, string> = {};
  Object.keys(groups).forEach((code) => {
    groups[code].sort((a, b) => a.at - b.at || a.i - b.i).forEach((g, idx) => {
      out[g.id] = idx === 0 ? code : `${code} ${idx + 1}`;
    });
  });
  return out;
};

// Market label for a test-user row. A child survey labels every row with itself (its
// list also holds rows stored under the parent). A parent resolves the row's stored
// child survey id, so same-country siblings stay JP / JP 2; a row stored under the
// parent itself names no market and shows a dash.
export const testUserMarket = (
  row: { storedSurveyId?: string | number | null },
  survey: { id: string | number; parentid?: string | number | null },
  labels: Record<string, string>,
): string => {
  const id = survey.parentid ? survey.id : row.storedSurveyId;
  return (id != null && labels[String(id)]) || '-';
};

// getOneDetails resolves to res.data; the survey sits in .data.data or .data
// depending on the endpoint version.
export const unwrapSurvey = (response: any): any => {
  if (response && response.data && response.data.data) return response.data.data;
  if (response && response.data) return response.data;
  return response;
};

// Test-user rows on one page of getOneDetails' user list. The list puts every
// admin-local Test row (source 'test': the rows Test / Copy Test Link create)
// ahead of the country panelist rows, newest first, so the Test rows are a
// prefix. `complete` is true once the prefix ends on this page, i.e. the count
// so far is final. Rows from an older backend without `source` fall back to
// surveyType.
const isLocalTestRow = (u: any) => (u && u.source ? u.source === 'test' : u?.surveyType === 'Test');
export const leadingTestRows = (users: any[], pageSize: number): { count: number; complete: boolean } => {
  const list = Array.isArray(users) ? users : [];
  let count = 0;
  while (count < list.length && isLocalTestRow(list[count])) count += 1;
  return { count, complete: count < list.length || list.length < pageSize };
};

const DAY = 24 * 60 * 60 * 1000;
export const fieldWindow = (publishDate?: string, expiryDate?: string, now: Date = new Date()) => {
  if (!expiryDate) return { daysLeft: null as number | null };
  const end = new Date(expiryDate).getTime();
  if (isNaN(end)) return { daysLeft: null as number | null };
  return { daysLeft: Math.max(0, Math.floor((end - now.getTime()) / DAY)) };
};

// Completes-weighted median LOI across a parent and its children; same rule as
// DashboardSurvey.aggregateChildrenStats (finalLOI weighted by completed_count).
export const weightedLoi = (rows: { completed_count?: number | string; finalLOI?: number | string }[]): number | null => {
  let completes = 0;
  let sum = 0;
  rows.forEach((r) => {
    const c = Number(r.completed_count) || 0;
    completes += c;
    sum += c * (Number(r.finalLOI) || 0);
  });
  return completes > 0 ? sum / completes : null;
};

export const SUMMARY_COLUMNS = [
  'Project Name', 'Project Id', 'Country', 'Client', 'Status', 'PM',
  'Client Quota', 'Complete', 'Start Date', 'End Date',
];
export const ALL_FIELDWORK_COLUMNS = [
  'Project Name', 'Project Id', 'Survey Unique Id', 'Country', 'Status', 'Client Quota', 'Complete',
  'Terminate', 'Quotafull', 'Quality Terminate', 'PQ Terminated', 'Verisoul Blocked',
  'Hits', 'Dropouts%', 'IR %', 'Median LOI', 'Complete Points',
];
